import { Suspense, useEffect, useMemo, useState } from 'react';
import {
  useGetBenchmarkChartDataQuery,
  useGetPortfolioAnalysisHeatQuery,
  useGetPortfolioAnalysisPowerQuery,
  useGetPortfolioAnalysisWaterQuery,
  useGetPortfolioHeatMetersQuery,
  useGetPortfolioPowerMetersQuery,
  useGetPortfolioWaterMetersQuery,
} from '../../../api/energy-analysis/portfolioAnalysisApiSlice';
import {
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
  DialogContent,
  Dialog,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';
import { showSnackbar } from '../../../redux/snackbarSlice';
import MeterContainer from './MeterContainer';
import { useLocation } from 'react-router-dom';
import PortfolioAnalysisBenchmarkChart from '@/features/portfolio-analysis/components/PortfolioAnalysisBenchmarkChart';
import PortfolioAnalysisDataGrid from '@/features/portfolio-analysis/components/datagrid/PortfolioAnalysisDataGrid';

const PortfolioAnalysisPage = () => {
  const account = useSelector(getSelectedAccount);

  const location = useLocation();
  const dispatch = useDispatch();

  const analysisType = useMemo(() => {
    if (location.pathname.includes('heat')) {
      return 'heat';
    } else if (location.pathname.includes('water')) {
      return 'water';
    } else {
      return 'power';
    }
  }, [location]);

  const preset = useMemo(() => {
    let result = 0;
    ['overview', 'dailyAnalysis', 'trendAnalysis', 'detailed'].forEach(
      (preset, index) => {
        if (location.pathname.includes(preset)) result = index;
      },
    );
    return result;
  }, [location]);

  const {
    data: powerData,
    error: powerError,
    isLoading: isPowerLoading,
    isFetching: isPowerFetching,
  } = useGetPortfolioAnalysisPowerQuery(
    { recalc_account_id: account?.AccountId },
    { skip: !account?.AccountId },
  );
  const {
    data: waterData,
    error: waterError,
    isLoading: isWaterLoading,
    isFetching: isWaterFetching,
  } = useGetPortfolioAnalysisWaterQuery(
    { recalc_account_id: account?.AccountId },
    { skip: !account?.AccountId },
  );
  const {
    data: heatData,
    error: heatError,
    isLoading: isHeatLoading,
    isFetching: isHeatFetching,
  } = useGetPortfolioAnalysisHeatQuery(
    { recalc_account_id: account?.AccountId },
    { skip: !account?.AccountId },
  );

  const { data: powerMeterData } = useGetPortfolioPowerMetersQuery(
    { recalc_account_id: account?.AccountId },
    {
      skip: !account?.AccountId,
      pollingInterval: 1000 * 60 * 5,
      skipPollingIfUnfocused: true,
    },
  );
  const { data: waterMeterData } = useGetPortfolioWaterMetersQuery(
    { recalc_account_id: account?.AccountId },
    {
      skip: !account?.AccountId,
      pollingInterval: 1000 * 60 * 5,
      skipPollingIfUnfocused: true,
    },
  );
  const { data: heatMeterData } = useGetPortfolioHeatMetersQuery(
    { recalc_account_id: account?.AccountId },
    {
      skip: !account?.AccountId,
      pollingInterval: 1000 * 60 * 5,
      skipPollingIfUnfocused: true,
    },
  );
  const rawData =
    analysisType === 'power'
      ? powerData
      : analysisType === 'heat'
        ? heatData
        : waterData;

  const meterData =
    analysisType === 'power'
      ? powerMeterData
      : analysisType === 'heat'
        ? heatMeterData
        : waterMeterData;

  const data = useMemo(() => {
    // power: pass straight through
    if (analysisType === 'power' || !rawData?.portfolio_analysis_list) {
      return rawData;
    }

    // heat or water: add metering_point_id once
    return {
      ...rawData,
      portfolio_analysis_list: rawData.portfolio_analysis_list.map((row) => ({
        ...row,
        metering_point_id: JSON.parse(row.data_stream).unit_id,
      })),
    };
  }, [rawData, analysisType]);

  const error = (() => {
    if (analysisType === 'power') return powerError;
    if (analysisType === 'heat') return heatError;
    if (analysisType === 'water') return waterError;
  })();

  const isLoading = (() => {
    if (analysisType === 'power') return isPowerLoading;
    if (analysisType === 'heat') return isHeatLoading;
    if (analysisType === 'water') return isWaterLoading;
  })();
  const isFetching = (() => {
    if (analysisType === 'power') return isPowerFetching;
    if (analysisType === 'heat') return isHeatFetching;
    if (analysisType === 'water') return isWaterFetching;
  })();

  const enrichedData = useMemo(() => {
    if (data == null) return null;

    let portfolioList = data.portfolio_analysis_list.filter(
      (item) => item.matrikel_key,
    );

    let temp = {
      ...data,
      portfolio_analysis_list: portfolioList.map((o) => {
        let meterEntry =
          meterData?.find(
            (b) => `${b.EjerlavKode}_${b.MatrikelNr}` == o.matrikel_key,
          ) || {};
        return {
          ...o,
          ...meterEntry,
          type: analysisType,
        };
      }),
    };
    return temp;
  }, [data, meterData, analysisType]);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState();

  const handleSetSelectedMeter = (meter) => {
    setIsOpen(true);
    setSelectedMeter(meter);
  };

  const [meterType, setMeterType] = useState('');

  const [selectedBenchmark, setSelectedBenchmark] = useState({});
  const {
    data: benchmarkChartData,
    isError: isBenchmarkChartDataError,
    isFetching: isBenchmarkChartDataFetching,
  } = useGetBenchmarkChartDataQuery(
    {
      matrikelKey: selectedBenchmark?.matrikel_key,
      accountId: account?.AccountId,
    },
    { skip: !selectedBenchmark?.matrikel_key },
  );

  useEffect(() => {
    if (isBenchmarkChartDataError)
      dispatch(
        showSnackbar({
          message: 'Der skete en uventet fejl ved hentning af data',
          severity: 'error',
        }),
      );
  }, [isBenchmarkChartDataError, dispatch]);

  useEffect(() => {
    if (isBenchmarkChartDataError)
      dispatch(
        showSnackbar({
          message: 'Der skete en uventet fejl ved hentning af data',
          severity: 'error',
        }),
      );
  }, [isBenchmarkChartDataError, dispatch]);
  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '80vh',
            verticalAlign: 'center',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 4 }}>
            Henter analyse...
          </Typography>
        </Box>
      ) : (
        <>
          {error && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Card
                width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}
                variant="outlined"
                sx={{ p: 2, mb: 2, mt: 8, borderRadius: 2 }}
              >
                <CardContent
                  width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}
                >
                  <Typography variant="h6">Ingen analyse fundet</Typography>
                  <Typography variant="body1" color="text.secondary">
                    Der er ikke fundet en analyse på denne konto.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {enrichedData && !error && (
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '80vh',
                    verticalAlign: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <Box
                minHeight={'50vh'}
                maxHeight={'80vh'}
                display={'flex'}
                flexDirection={'column'}
              >
                <PortfolioAnalysisDataGrid
                  key={analysisType}
                  data={enrichedData}
                  setSelectedBenchmark={setSelectedBenchmark}
                  setSelectedMeter={handleSetSelectedMeter}
                  setMeterType={setMeterType}
                  analysisType={analysisType}
                  isLoading={isFetching}
                  chosenPreset={preset}
                />
              </Box>
            </Suspense>
          )}

          <Dialog
            maxWidth={false}
            open={!!selectedBenchmark?.matrikel_key}
            onClose={() => setSelectedBenchmark({})}
          >
            <DialogContent>
              <Box>
                <Suspense
                  fallback={
                    <Box
                      style={{
                        width: '60vw',
                        height: '30vh',
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        verticalAlign: 'center',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  }
                >
                  <PortfolioAnalysisBenchmarkChart
                    key={preset}
                    data={benchmarkChartData}
                    address={selectedBenchmark.address}
                    isLoading={isBenchmarkChartDataFetching}
                  />
                </Suspense>
              </Box>
            </DialogContent>
          </Dialog>
        </>
      )}
      {selectedMeter && (
        <MeterContainer
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setSelectedMeter(null);
          }}
          meter={selectedMeter}
          meterType={meterType}
          analysisType={analysisType}
        />
      )}
    </>
  );
};

export default PortfolioAnalysisPage;
